import MappingTopBox from "../CreatorTools/MappingTool/components/MappingTopBox";
import { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import type { ManualEstimateReviewTracker } from "@rebuild/shared";
import { ManualEstimateReviewStage, ManualEstimateReviewUploadSource } from "@rebuild/shared";
import { useOrganizationSelect } from "../../hooks/useOrganizationSelect";
import {
  createManualEstimateReview,
  fetchManualEstimateReviewTrackers,
  updateManualEstimateReviewTrackerStage,
} from "./manualEstimateReviewsAPI";
import ManualEstimateReviewsTable from "./ManualEstimateReviewsTable";
import AddManualEstimateReviewModal from "./AddManualEstimateReviewModal";
import EditManualEstimateReviewModal from "./EditManualEstimateReviewModal";

const CREATION_POLL_INTERVAL_MS = 2000;
const CREATION_POLL_MAX_ATTEMPTS = 15;
const PAGE_SIZE = 50;

function ManualEstimateReviewsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <MappingTopBox />
      <div className="ml-8 mr-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2 mt-4">Manual Estimate Reviews</h1>
        <p className="text-gray-600 mb-6">
          Create manual estimate reviews from production estimates and manage them through their review stages.
        </p>
        <ManualEstimateReviewsComponent />
      </div>
    </div>
  );
}

function ManualEstimateReviewsComponent() {
  const [trackers, setTrackers] = useState<ManualEstimateReviewTracker[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [selectedTracker, setSelectedTracker] = useState<ManualEstimateReviewTracker | undefined>();
  const [stageFilter, setStageFilter] = useState<ManualEstimateReviewStage | "">("");

  const { organizations } = useOrganizationSelect({ includeTestOrgs: true });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await fetchManualEstimateReviewTrackers({ stage: stageFilter || undefined, page, limit: PAGE_SIZE });
      setTrackers(result.data);
      setTotal(result.total);
    } catch (e) {
      toast.error("Failed to load manual estimate reviews");
    } finally {
      setIsLoading(false);
    }
  }, [stageFilter, page]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onStageFilterChange = (value: ManualEstimateReviewStage | "") => {
    setStageFilter(value);
    setPage(1);
  };

  // Creation is async: the POST enqueues a job and the tracker only appears once a worker finishes it,
  // so we poll the (unfiltered) first page — where the newest tracker sorts — rather than refreshing immediately.
  const pollForCreatedTracker = useCallback(async (sourceEstimateId: string): Promise<boolean> => {
    for (let attempt = 0; attempt < CREATION_POLL_MAX_ATTEMPTS; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, CREATION_POLL_INTERVAL_MS));
      try {
        const latest = await fetchManualEstimateReviewTrackers({ page: 1, limit: PAGE_SIZE });
        setTrackers(latest.data);
        setTotal(latest.total);
        if (latest.data.some((tracker) => tracker.originalEstimateId === sourceEstimateId)) {
          return true;
        }
      } catch {
        // Ignore transient fetch errors and keep polling.
      }
    }
    return false;
  }, []);

  const onCreate = async (sourceOrgId: string, sourceEstimateId: string) => {
    await createManualEstimateReview({ sourceOrgId, sourceEstimateId, uploadSource: ManualEstimateReviewUploadSource.ToolsUi });
    setIsAdding(false);
    // Jump to the unfiltered first page so the newly created tracker is visible once it lands.
    setStageFilter("");
    setPage(1);
    toast.info("Processing estimate… this can take a few seconds.");
    const created = await pollForCreatedTracker(sourceEstimateId);
    if (created) {
      toast.success("Manual estimate review created");
    } else {
      toast.info("Still processing. Wait a few seconds, then refresh the page.");
    }
  };

  const onUpdateStage = async (uuid: string, stage: ManualEstimateReviewStage) => {
    await updateManualEstimateReviewTrackerStage(uuid, stage);
    await refresh();
    setSelectedTracker(undefined);
    toast.success("Stage updated");
  };

  return (
    <>
      <div className="bg-white p-6 rounded-lg shadow">
        <p className="text-sm text-gray-500 mb-4">
          There might be some estimates being processed in the background and may take a few seconds to appear. If you don't see an estimate
          you think should be here, wait a couple of seconds and then refresh the page.
        </p>
        <div className="flex justify-between items-end mb-4">
          <div className="min-w-[220px]">
            <label className="block text-sm font-medium text-gray-700 mb-2">Filter by Stage</label>
            <select
              value={stageFilter}
              onChange={(e) => onStageFilterChange(e.target.value as ManualEstimateReviewStage | "")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All stages</option>
              {Object.values(ManualEstimateReviewStage).map((stage) => (
                <option key={stage} value={stage}>
                  {stage}
                </option>
              ))}
            </select>
          </div>
          <button
            disabled={isLoading}
            onClick={() => setIsAdding(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            Add Manual Estimate Review
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : total === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <p className="text-gray-500">
              {stageFilter ? `No manual estimate reviews in the "${stageFilter}" stage.` : "No manual estimate reviews yet."}
            </p>
          </div>
        ) : (
          <>
            <ManualEstimateReviewsTable trackers={trackers} onSelect={setSelectedTracker} />
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-gray-500">{total} total</p>
              <div className="flex items-center gap-3">
                <button
                  disabled={page <= 1 || isLoading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  disabled={page >= totalPages || isLoading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {isAdding && <AddManualEstimateReviewModal organizations={organizations} onClose={() => setIsAdding(false)} onSubmit={onCreate} />}

      {selectedTracker && (
        <EditManualEstimateReviewModal tracker={selectedTracker} onClose={() => setSelectedTracker(undefined)} onSave={onUpdateStage} />
      )}
    </>
  );
}

export default ManualEstimateReviewsPage;
